import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChefHat, Warehouse, Snowflake, Truck, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

gsap.registerPlugin(ScrollTrigger);

const SupplyChainSection = () => {
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];

  const iconMap = {
    ChefHat,
    Warehouse,
    Snowflake,
    Truck
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.supply-card',
        { opacity: 0, y: 60, rotateX: 45 },
        {
          opacity: 1,
          y: 0,
          rotateX: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%'
          }
        }
      );

      gsap.fromTo('.connect-line',
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 0.5,
          stagger: 0.2,
          delay: 0.5,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [language]);

  const benefits = language === 'en' ? [
    'Centralized raw material supply',
    'Quality consistency across all outlets',
    'Cost control advantage for franchisees',
    'Same-day delivery to all locations'
  ] : [
    'मध्यवर्ती कच्चा माल पुरवठा',
    'सर्व आउटलेट्सवर गुणवत्तेचे सातत्य',
    'फ्रँचायझीधारकांसाठी खर्च नियंत्रण फायदा',
    'सर्व ठिकाणी एकाच दिवशी वितरण'
  ];

  const icons = ['ChefHat', 'Warehouse', 'Snowflake', 'Truck'];

  return (
    <section ref={sectionRef} className="py-20 lg:py-32 bg-white dark:bg-black transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.supplyChain.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.supplyChain.title} <span className="text-red-600 dark:text-red-500">{t.supplyChain.highlight}</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.supplyChain.subtitle}
          </p>
        </div>

        <div className="relative mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.supplyChain.items.map((item, index) => {
              const IconComponent = iconMap[icons[index]];
              return (
                <div key={index} className="relative">
                  <div className="supply-card bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 dark:border-gray-700 text-center h-full">
                    <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-bold shadow-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {index + 1}
                    </div>

                    <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-red-100 to-yellow-100 dark:from-red-900/30 dark:to-yellow-900/30 flex items-center justify-center mb-6">
                      {IconComponent && <IconComponent className="w-10 h-10 text-red-600 dark:text-red-400" />}
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {item.description}
                    </p>
                  </div>

                  {index < t.supplyChain.items.length - 1 && (
                    <div className="connect-line hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-red-400 to-yellow-400 origin-left transform -translate-y-1/2 z-10">
                      <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-8 border-l-yellow-400 border-y-4 border-y-transparent"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12 shadow-xl border border-gray-800">
          <h3 className="text-2xl font-bold text-white mb-8 text-center" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {language === 'en' ? 'Franchisee Benefits' : 'फ्रँचायझीचे फायदे'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <p className="text-white/90 text-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {benefit}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SupplyChainSection;
